import { userActionTypeEnum } from '@thoughtspot/metrics';
import { AccordionVariant } from '@thoughtspot/radiant-react/widgets/accordion';
import { PICKER_VIEW } from '@thoughtspot/radiant-react/widgets/colorpicker/colorpicker-menu';
import { getAxisPropertiesById } from '../../../core/chart-model-utils';
import styles from '../../../styles/chart-settings-interface.module.scss';
import { ChartSettingsElementType, } from '../chart-settings-v2-schema.types';
import { getExtendedChartColorPalette } from './chart-color-palette-utils';
import { shouldHideIntervalFromatter, shouldHideMarkerFormatter, } from './common-rules';
import { getMixpanelConfigForIntervalStyle, getMixpanelConfigForMarkerStyle, } from './mixpanel-utils';
export const createElement = (elementType, options = {}) => {
    const result = {
        elementType,
        key: options.key || '',
        clientStatePath: options.clientStatePath || '',
        properties: options.properties || {},
        value: options.value || null,
        className: options.className || '',
        validatorFunction: options.validatorFunction ||
            (() => ({ isValid: true, errorMessage: '' })),
    };
    Object.keys(options).forEach(key => {
        if (key !== 'elementType' &&
            key !== 'key' &&
            key !== 'clientStatePath' &&
            key !== 'properties' &&
            key !== 'value' &&
            key !== 'className' &&
            key !== 'validatorFunction') {
            result[key] = options[key];
        }
    });
    return result;
};
export const createTextInput = (options = {}) => {
    return createElement(ChartSettingsElementType.TEXT_INPUT, options);
};
export const createCheckbox = (options = {}) => {
    return createElement(ChartSettingsElementType.CHECKBOX, options);
};
export const createCheckboxGroup = (options = {}) => {
    return createElement(ChartSettingsElementType.CHECKBOX_GROUP, options);
};
export const createPositionControl = (options = {}) => {
    return createElement(ChartSettingsElementType.POSITION_CONTROL, options);
};
export const createDropdown = (options = {}) => {
    return createElement(ChartSettingsElementType.DROPDOWN, options);
};
export const createMultilevelDropdown = (options = {}) => {
    return createElement(ChartSettingsElementType.MULTILEVEL_DROPDOWN, options);
};
export const createColorPicker = (options = {}) => {
    return createElement(ChartSettingsElementType.COLOR_PICKER, options);
};
export const createNumberInput = (options = {}) => {
    return createElement(ChartSettingsElementType.NUMBER_INPUT, options);
};
export const createNumberRange = (options = {}) => {
    return createElement(ChartSettingsElementType.NUMBER_RANGE, options);
};
export const createRadioGroup = (options = {}) => {
    return createElement(ChartSettingsElementType.RADIO_GROUP, options);
};
export const createRadio = (options = {}) => {
    return createElement(ChartSettingsElementType.RADIO, options);
};
export const createAccordion = (options = {}) => {
    const properties = {
        variant: AccordionVariant.MINIMAL,
        expandedItemsIndexes: [0],
        expandMultipleItems: true,
        useContainedHeight: false,
        noPadding: true,
        useUncontrolledState: true,
    };
    if (options.properties) {
        Object.keys(options.properties).forEach(key => {
            properties[key] = options.properties[key];
        });
    }
    const result = createElement(ChartSettingsElementType.ACCORDION, options);
    result.properties = properties;
    return result;
};
export const createAccordionItem = (options = {}) => {
    var _a, _b, _c;
    const properties = {
        header: ((_a = options.properties) === null || _a === void 0 ? void 0 : _a.header) || '',
        isExpanded: (_c = (_b = options.properties) === null || _b === void 0 ? void 0 : _b.isExpanded) !== null && _c !== void 0 ? _c : true,
        alignToHeader: false,
        className: styles.accordionItemNoMargin,
        headerClassName: styles.accordionHeaderStyle,
        noPadding: true,
        showHeaderAsTooltip: true,
    };
    if (options.properties) {
        Object.keys(options.properties).forEach(key => {
            if (key !== 'header' &&
                key !== 'isExpanded' &&
                key !== 'alignToHeader' &&
                key !== 'headerClassName' &&
                key !== 'noPadding' &&
                key !== 'showHeaderAsTooltip') {
                properties[key] = options.properties[key];
            }
        });
    }
    const result = createElement(ChartSettingsElementType.ACCORDION_ITEM, options);
    result.properties = properties;
    return result;
};
export const createSection = (options = {}) => {
    return createElement(ChartSettingsElementType.SECTION, options);
};
export const createLabelledView = (options = {}) => {
    return createElement(ChartSettingsElementType.LABELLED_VIEW, options);
};
export const createInheritanceWidget = (options = {}) => {
    return createElement(ChartSettingsElementType.INHERITANCE_WIDGET, options);
};
export const createTooltipConfigurator = (options = {}) => {
    return createElement(ChartSettingsElementType.TOOLTIP_CONFIGURATOR, options);
};
export const createConditionalFormatter = (options = {}) => {
    return createElement(ChartSettingsElementType.CONDITIONAL_FORMATTER, options);
};
export const createChartZoom = (options = {}) => {
    return createElement(ChartSettingsElementType.CHART_ZOOM, options);
};
export const createBackgroundImageEditor = (options = {}) => {
    return createElement(ChartSettingsElementType.BACKGROUND_IMAGE_EDITOR, {
        key: 'plotBackgroundImage',
        getMixpanelConfig: () => {
            return {
                eventName: userActionTypeEnum.ADD_IMAGE_TO_CHART,
            };
        },
        ...options,
    });
};
export const createNumericFilterInput = (options = {}) => {
    return createElement(ChartSettingsElementType.NUMERIC_FILTER_INPUT, options);
};
export const createColumnRenameInput = (options = {}) => {
    return createElement(ChartSettingsElementType.COLUMN_RENAME_INPUT, options);
};
export const createTableColumnRenameInput = (options = {}) => {
    return createElement(ChartSettingsElementType.TABLE_COLUMN_RENAME_INPUT, options);
};
export const createSortFormat = (options = {}) => {
    return createElement(ChartSettingsElementType.SORT_FORMAT, options);
};
export const createAxisRenameInput = (options = {}) => {
    return createElement(ChartSettingsElementType.AXIS_RENAME_INPUT, options);
};
export const createFontFormatter = (options = {}) => {
    var _a;
    const value = {
        size: '12',
        color: '#5E5E5E',
        style: [],
    };
    if ((_a = options === null || options === void 0 ? void 0 : options.properties) === null || _a === void 0 ? void 0 : _a.value) {
        Object.keys(options.properties.value).forEach(key => {
            var _a;
            value[key] = ((_a = options.properties) === null || _a === void 0 ? void 0 : _a.value)[key];
        });
    }
    const properties = { ...(options.properties || {}) };
    properties.value = value;
    const result = createElement(ChartSettingsElementType.FONT_FORMATTER, options);
    result.properties = properties;
    return result;
};
export const createNumberFormatter = (options = {}) => {
    var _a, _b, _c;
    const typeProps = {
        ariaLabel: 'number-type',
    };
    if ((_a = options.properties) === null || _a === void 0 ? void 0 : _a.typeProps) {
        Object.keys(options.properties.typeProps).forEach(key => {
            var _a;
            typeProps[key] = ((_a = options.properties) === null || _a === void 0 ? void 0 : _a.typeProps)[key];
        });
    }
    const subTypeProps = {
        ariaLabel: 'number-subtype',
    };
    if ((_b = options.properties) === null || _b === void 0 ? void 0 : _b.subTypeProps) {
        Object.keys(options.properties.subTypeProps).forEach(key => {
            var _a;
            subTypeProps[key] = ((_a = options.properties) === null || _a === void 0 ? void 0 : _a.subTypeProps)[key];
        });
    }
    const formatProps = {
        ariaLabel: 'number-format',
    };
    if ((_c = options.properties) === null || _c === void 0 ? void 0 : _c.formatProps) {
        Object.keys(options.properties.formatProps).forEach(key => {
            var _a;
            formatProps[key] = ((_a = options.properties) === null || _a === void 0 ? void 0 : _a.formatProps)[key];
        });
    }
    const properties = {
        variant: 'font',
        typeProps,
        subTypeProps,
        formatProps,
    };
    if (options.properties) {
        Object.keys(options.properties).forEach(key => {
            if (key !== 'typeProps' &&
                key !== 'subTypeProps' &&
                key !== 'formatProps') {
                properties[key] = options.properties[key];
            }
        });
    }
    const result = createElement(ChartSettingsElementType.NUMBER_FORMATTER, options);
    result.properties = properties;
    return result;
};
export const createLineFormatter = (options = {}) => {
    var _a;
    const value = {
        dashStyle: 'SOLID_LINE',
        thickness: 2,
        color: '',
    };
    if ((_a = options.properties) === null || _a === void 0 ? void 0 : _a.value) {
        Object.keys(options.properties.value).forEach(key => {
            var _a;
            value[key] = ((_a = options.properties) === null || _a === void 0 ? void 0 : _a.value)[key];
        });
    }
    const properties = {
        thicknessProps: {},
        colorProps: {},
        typeProps: {},
        value,
    };
    if (options.properties) {
        Object.keys(options.properties).forEach(key => {
            if (key !== 'value') {
                properties[key] = options.properties[key];
            }
        });
    }
    const result = createElement(ChartSettingsElementType.LINE_FORMATTER, options);
    result.properties = properties;
    return result;
};
export const createBackgroundFormatter = (options = {}) => {
    const extendedPaletteColors = getExtendedChartColorPalette();
    const properties = {
        borderColorProps: {
            showEmptyColor: true,
            colors: extendedPaletteColors,
            viewType: PICKER_VIEW.EXTENDED,
        },
        backgroundColorProps: {
            showEmptyColor: true,
            colors: extendedPaletteColors,
            viewType: PICKER_VIEW.EXTENDED,
        },
        value: {
            backgroundColor: '',
            borderColor: '',
            backgroundOpacity: 0,
        },
    };
    if (options.properties) {
        Object.keys(options.properties).forEach(key => {
            properties[key] = options.properties[key];
        });
    }
    const result = createElement(ChartSettingsElementType.BACKGROUND_FORMATTER, options);
    result.properties = properties;
    return result;
};
export const createIntervalFormatter = () => {
    const extendedPaletteColors = getExtendedChartColorPalette();
    return createElement(ChartSettingsElementType.INTERVAL_FORMATTER, {
        key: 'intervals',
        properties: {
            value: {
                length: '5',
                thickness: '1',
                color: '#5E5E5E',
            },
            colorProps: {
                colors: extendedPaletteColors,
                viewType: PICKER_VIEW.EXTENDED,
            },
        },
        isHiddenCallback: shouldHideIntervalFromatter,
        getMixpanelConfig: ({ updatedValue, previousValue, config, vizModel, }) => {
            var _a;
            const { axisId } = config.metadata;
            const axisType = (_a = getAxisPropertiesById(vizModel.vizProp, axisId)) === null || _a === void 0 ? void 0 : _a.axisType;
            const mixpanelConfig = getMixpanelConfigForIntervalStyle(updatedValue, previousValue);
            return {
                ...mixpanelConfig,
                properties: {
                    axisType,
                },
            };
        },
    });
};
export const createMarkerFormatter = () => {
    return createElement(ChartSettingsElementType.MARKER_FORMATTER, {
        key: 'markerStyle',
        properties: {
            value: {
                type: 'circle',
                size: '5',
            },
        },
        parseValueFromComponent: value => {
            if ((value === null || value === void 0 ? void 0 : value.size) !== null) {
                return {
                    ...value,
                    size: parseInt(value.size, 10),
                };
            }
            return value;
        },
        isHiddenCallback: shouldHideMarkerFormatter,
        isAdvanced: true,
        getMixpanelConfig: ({ updatedValue, previousValue }) => {
            return getMixpanelConfigForMarkerStyle(updatedValue, previousValue);
        },
    });
};
//# sourceMappingURL=elements.js.map